// Date/text formatting helpers shared across v2 pages.

// Subpath import — date-fns v4's root barrel breaks under TypeScript 4.9
// (see comment in legacy Dashboard.tsx).
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { describeError, errorToPlainText } from './errorFormat';

// Matches an ISO-ish "YYYY-MM-DD[ T]HH:MM[:SS][.fff]" prefix (with or without
// a trailing timezone). Backend timestamps arrive in several shapes:
//   - "2026-06-12T19:52:00Z" / "...+00:00"        (already UTC, parses fine)
//   - "2026-06-12 19:52:00 UTC"                   (space + literal UTC)
//   - "2026-06-12T19:52:00"                       (UTC, but no marker)
//   - "Thu Jun 12 13:52:00 MDT 2026"              (Mountain, named tz)
const ISO_LIKE = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/;

/**
 * Parse a backend timestamp into a Date. ISO-like values without a timezone are
 * treated as UTC (the backend emits UTC); a literal " UTC" suffix is honored.
 * Other shapes (e.g. "Thu Jun 12 13:52:00 MDT 2026") carry their own tz and are
 * parsed natively. All callers then render in Mountain time.
 */
export const parseTimestamp = (timestamp: string): Date => {
  const s = timestamp.trim();
  if (ISO_LIKE.test(s)) {
    let v = s.replace(' ', 'T').replace(/\s*UTC$/i, 'Z');
    if (!/[zZ]$|[+-]\d{2}:?\d{2}$/.test(v)) v += 'Z'; // no tz → assume UTC
    return new Date(v);
  }
  return new Date(s);
};

/** "3 minutes ago" — empty string for missing/invalid timestamps. */
export const timeAgo = (timestamp?: string): string => {
  if (!timestamp) return '';
  const date = parseTimestamp(timestamp);
  if (isNaN(date.getTime())) return '';
  try {
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return '';
  }
};

/** Absolute timestamp in Mountain time, matching the legacy dashboard. */
export const formatTimestamp = (timestamp?: string): string => {
  if (!timestamp) return '';
  try {
    return parseTimestamp(timestamp).toLocaleString('en-US', {
      timeZone: 'America/Denver',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return timestamp;
  }
};

/** Compact absolute Mountain time, e.g. "06/17 13:45". */
export const formatTimestampShort = (timestamp?: string): string => {
  if (!timestamp) return '';
  try {
    return parseTimestamp(timestamp).toLocaleString('en-US', {
      timeZone: 'America/Denver',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return timestamp;
  }
};

/** Truncate long ids for display (keeps head, adds ellipsis). */
export const shortId = (id?: string, length = 12): string => {
  if (!id) return '';
  return id.length > length ? `${id.slice(0, length)}…` : id;
};

/**
 * Flatten a record's `error` payload to plain text — "title — summary" then the
 * details — for copy-to-clipboard and simple displays. '' when there is no error.
 */
export const formatError = (error: unknown): string => {
  const view = describeError(error);
  return view ? errorToPlainText(view) : '';
};
